// 🔧 store_cart.js
// ✅ Purpose: Public Store cart page
// ✅ Features:
// - Loads event-scoped cart from localStorage
// - Shows cart items, quantities, and subtotal
// - Allows removing items
// - Allows clearing cart
// - Checkout button is disabled until Stripe phase
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import {
  clearStoreCart,
  getStoreCart,
  saveStoreCart,
} from "../../utils/store_cart_helpers";
import {
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
    getFunctions,
    httpsCallable,
} from "firebase/functions";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import ReservationCountdown from "../../components/commerce/reservation_countdown";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const StoreCart = () => {

    const {
        eventslug,
        eventId,
        eventDoc,
        eventLoading,
        ready,
    } = useEventContext({ redirectIfNotFound: true });
    const [storeSettings, setStoreSettings] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");
    const [reservation, setReservation] = useState(null);
    const [reservationReleased, setReservationReleased] = useState(false);
    const [searchParams] = useSearchParams();
    const storePaymentStatus = searchParams.get("store_payment") || "";

    useEffect(() => {

        async function releaseCancelledReservation() {

            // Reservation release will only occur if
            // we have an orderId from checkout.

            const orderId =
                searchParams.get("orderId");

            const reservationCancelToken =
                searchParams.get("reservationCancelToken");

            if (
                storePaymentStatus !== "cancelled" ||
                !eventId ||
                !orderId ||
                !reservationCancelToken ||
                reservationReleased
            ) {
                return;
            }

            try {

                const releaseReservation =
                    httpsCallable(
                        getFunctions(),
                        "releaseReservation"
                    );

                await releaseReservation({

                    eventId,

                    orderId,
                    reservationCancelToken,

                });

                console.log(
                    "✅ Reservation released."
                );

                setReservationReleased(true);

            }
            catch (err) {

                console.error(
                    "Failed to release reservation:",
                    err
                );

            }

        }

        releaseCancelledReservation();

    }, [

        eventId,

        reservationReleased,

        searchParams,

        storePaymentStatus,

    ]);

    // =====================================================
    // Phase 7
    // If checkout was cancelled,
    // call Commerce releaseReservation()
    // so inventory is returned immediately.
    // =====================================================    
    // =====================================================
    // 🧾 Checkout Information
    // =====================================================

    const [checkoutInfo, setCheckoutInfo] = useState({
    customerName: "",
    email: "",
    phone: "",

    fulfillmentMethod: "pickup",

    shippingName: "",
    shippingAddress1: "",
    shippingAddress2: "",
    shippingCity: "",
    shippingState: "",
    shippingPostalCode: "",

    orderNotes: "",
    });

  const centsToMoney = (cents) =>
    `$${(Number(cents || 0) / 100).toFixed(2)}`;

  useEffect(() => {
    if (!eventslug) return;
    setCartItems(getStoreCart(eventslug));
  }, [eventslug]);

  useEffect(() => {
    const loadStoreSettings = async () => {
        if (!eventId) return;

        try {
        const settingsSnap = await getDoc(
            doc(db, "events", eventId, "settings", "store")
        );

        setStoreSettings(settingsSnap.exists() ? settingsSnap.data() : {});
        } catch (err) {
        console.error("Failed to load Store settings:", err);
        setStoreSettings({});
        }
    };

    loadStoreSettings();
    }, [eventId]);

  const updateQuantity = (index, quantity) => {
    const nextItems = [...cartItems];

    nextItems[index] = {
      ...nextItems[index],
      quantity: Number(quantity || 1),
    };

    setCartItems(nextItems);
    saveStoreCart(eventslug, nextItems);
  };

  const removeItem = (index) => {
    const nextItems = cartItems.filter((_, itemIndex) => itemIndex !== index);

    setCartItems(nextItems);
    saveStoreCart(eventslug, nextItems);
  };

  const clearCart = () => {
    clearStoreCart(eventslug);
    setCartItems([]);
  };

    // =====================================================
    // ✏️ Checkout Form Handler
    // =====================================================

    const handleCheckoutInfoChange = (e) => {
    const { name, value } = e.target;

    setCheckoutInfo((prev) => ({
        ...prev,
        [name]: value,
    }));
    };

  const subtotalCents = cartItems.reduce(
    (sum, item) =>
      sum + Number(item.priceCents || 0) * Number(item.quantity || 0),
    0
  );

  const shippingCents =
    checkoutInfo.fulfillmentMethod === "shipping" &&
    storeSettings?.shippingEnabled === true &&
    !(
        Number(storeSettings?.freeShippingThresholdCents || 0) > 0 &&
        subtotalCents >= Number(storeSettings.freeShippingThresholdCents)
    )
        ? Number(storeSettings?.flatShippingCents || 0)
        : 0;

    const totalCents = subtotalCents + shippingCents;

    const handleCheckout = async () => {
        setCheckoutError("");

        if (!checkoutInfo.customerName || !checkoutInfo.email) {
            setCheckoutError("Please enter your name and email before checkout.");
            return;
        }

        if (checkoutInfo.fulfillmentMethod === "shipping") {
            if (
            !checkoutInfo.shippingAddress1 ||
            !checkoutInfo.shippingCity ||
            !checkoutInfo.shippingState ||
            !checkoutInfo.shippingPostalCode
            ) {
            setCheckoutError("Please complete the shipping address before checkout.");
            return;
            }
        }

        try {
            setCheckoutLoading(true);

            const functions = getFunctions();

            // =====================================================
            // 🛒 Commerce Engine Checkout
            // =====================================================

            const createCommerceCheckoutSession = httpsCallable(
                functions,
                "createCommerceCheckoutSession"
            );

            const response =
                await createCommerceCheckoutSession({

                    // Commerce Engine determines which adapter
                    // should process this checkout.
                    commerceType: "event_store",

                    eventId,

                    cartItems,

                    checkoutInfo,

                    subtotalCents,

                    shippingCents,

                    totalCents,

                });

            if (response?.data?.reservation) {

                // Security hardening: sensitive diagnostic payload intentionally omitted.

                setReservation(
                    response.data.reservation
                );

            }

            const checkoutUrl = response?.data?.url;

            if (!checkoutUrl) {
            throw new Error("Checkout URL was not returned.");
            }

            logger.log("✅ Store checkout session created", {
            eventId,
            });

            trackEvent({ action: GA_EVENTS.STORE_CHECKOUT_STARTED, parameters: { event_slug: eventslug, event_id: eventId, commerce_type: "store", source_page: window.location.pathname } });

            window.location.href = checkoutUrl;
        } catch (err) {
            logger.error("❌ Store checkout failed:", {
            value: err,
            });

            trackEvent({ action: GA_EVENTS.CHECKOUT_FAILED, parameters: { event_slug: eventslug, event_id: eventId, commerce_type: "store", failure_reason: "session_creation_failed", source_page: window.location.pathname } });

            setCheckoutError(
            err?.message || "Unable to start checkout. Please try again."
            );
        } finally {
            setCheckoutLoading(false);
        }
    };

  if (!ready || !eventId || eventLoading) {
    return <FullPageSpinner message="Loading Cart..." />;
  }

return (
  <>
    <SeoHead
      title={`Cart | ${eventDoc?.name || "Store"}`}
      description="Store shopping cart."
      path={`/${eventslug}/cart`}
      noindex={true}
    />

    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🛒 Cart</h1>

            <p className="lead text-muted mb-0">
              {eventDoc?.name || "Event Store"}
            </p>

            {storePaymentStatus === "cancelled" && (
            <div className="alert alert-warning mt-3">
                Checkout was cancelled. Your cart is still saved, and you can try again when ready.
            </div>
            )}

            {storePaymentStatus === "expired" && (
            <div className="alert alert-warning mt-3">
                Checkout expired before payment was completed. Your cart is still saved, but inventory availability may have changed.
            </div>
            )}

          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="card shadow-sm border-0">
            <div className="card-body text-center p-4">
              <div className="alert alert-info">
                Your cart is empty.
              </div>

              <Link
                to={`/${eventslug}/store`}
                className="btn btn-outline-primary"
              >
                Browse Store
              </Link>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            <div className="col-12 col-lg-8">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white">
                  <strong>Cart Items</strong>
                </div>

                <div className="card-body">
                  {cartItems.map((item, index) => (
                    <div
                      key={`${item.productId}-${item.variantId}`}
                      className="border rounded p-3 mb-3"
                    >
                      <div className="row g-3 align-items-center">
                        <div className="col-12 col-md-2">
                          <div
                            className="bg-light border rounded d-flex align-items-center justify-content-center"
                            style={{ minHeight: "90px" }}
                          >
                            {item.productImageUrl ? (
                              <img
                                src={item.productImageUrl}
                                alt={item.productName}
                                className="img-fluid"
                                style={{
                                  maxHeight: "80px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <span className="small text-muted">
                                No Image
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-md-5">
                          <div className="fw-bold">
                            {item.productName}
                          </div>

                          <div className="small text-muted">
                            {item.variantLabel}
                          </div>

                          {item.sku && (
                            <div className="small text-muted">
                              SKU: {item.sku}
                            </div>
                          )}
                        </div>

                        <div className="col-6 col-md-2">
                          <label className="form-label small">
                            Quantity
                          </label>

                          <input
                            type="number"
                            min="1"
                            max={Number(item.availableInventory || 1)}
                            className="form-control"
                            value={item.quantity}
                            onChange={(e) =>
                              updateQuantity(index, e.target.value)
                            }
                          />
                        </div>

                        <div className="col-6 col-md-2">
                          <div className="small text-muted">
                            Item Total
                          </div>

                          <div className="fw-bold">
                            {centsToMoney(
                              Number(item.priceCents || 0) *
                                Number(item.quantity || 0)
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-md-1 text-md-end">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeItem(index)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={clearCart}
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">

              {
                  reservation && (

                      <ReservationCountdown

                          expiresAt={
                              reservation.expiresAt
                          }

                          durationMinutes={
                              reservation.durationMinutes
                          }

                          onExpired={() => {

                              console.log(
                                  "Reservation expired."
                              );

                              setReservation(null);

                              //
                              // Phase 7:
                              // Call releaseReservation()
                              // Refresh cart
                              //

                          }}

                      />

                  )
              }

              <div className="card shadow-sm border-0">

                <div className="card-header bg-white">
                  <strong>Order Summary</strong>
                </div>

                <div className="card-body">

                    <div className="d-flex justify-content-between mb-3">
                        <span>Subtotal</span>
                        <strong>{centsToMoney(subtotalCents)}</strong>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                    <span>Shipping</span>
                    <strong>{centsToMoney(shippingCents)}</strong>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between mb-3">
                    <span>Total</span>
                    <strong>{centsToMoney(totalCents)}</strong>
                    </div>

                    <hr />

                    <h6 className="fw-bold mb-3">
                        Customer Information
                    </h6>

                    <div className="mb-3">
                        <label className="form-label">
                        Full Name
                        </label>

                        <input
                        type="text"
                        className="form-control"
                        name="customerName"
                        value={checkoutInfo.customerName}
                        onChange={handleCheckoutInfoChange}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                        Email
                        </label>

                        <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={checkoutInfo.email}
                        onChange={handleCheckoutInfoChange}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                        Phone
                        </label>

                        <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={checkoutInfo.phone}
                        onChange={handleCheckoutInfoChange}
                        />
                    </div>

                    <hr />

                    <h6 className="fw-bold mb-3">
                        Fulfillment
                    </h6>

                    <div className="mb-3">
                        <label className="form-label">
                        Order Method
                        </label>

                        <select
                        className="form-select"
                        name="fulfillmentMethod"
                        value={checkoutInfo.fulfillmentMethod}
                        onChange={handleCheckoutInfoChange}
                        >
                            {storeSettings?.pickupEnabled !== false && (
                            <option value="pickup">
                                Pickup at Event
                            </option>
                            )}

                            {storeSettings?.shippingEnabled === true && (
                            <option value="shipping">
                                Ship to Address
                            </option>
                            )}
                        </select>
                    </div>

                    {checkoutInfo.fulfillmentMethod === "shipping" && (
                        <>
                        <div className="mb-3">
                            <label className="form-label">
                            Recipient Name
                            </label>

                            <input
                            type="text"
                            className="form-control"
                            name="shippingName"
                            value={checkoutInfo.shippingName}
                            onChange={handleCheckoutInfoChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                            Address Line 1
                            </label>

                            <input
                            type="text"
                            className="form-control"
                            name="shippingAddress1"
                            value={checkoutInfo.shippingAddress1}
                            onChange={handleCheckoutInfoChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="form-label">
                            Address Line 2
                            </label>

                            <input
                            type="text"
                            className="form-control"
                            name="shippingAddress2"
                            value={checkoutInfo.shippingAddress2}
                            onChange={handleCheckoutInfoChange}
                            />
                        </div>

                        <div className="row g-2">
                            <div className="col-md-5">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="City"
                                name="shippingCity"
                                value={checkoutInfo.shippingCity}
                                onChange={handleCheckoutInfoChange}
                            />
                            </div>

                            <div className="col-md-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="State"
                                name="shippingState"
                                value={checkoutInfo.shippingState}
                                onChange={handleCheckoutInfoChange}
                            />
                            </div>

                            <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Zip"
                                name="shippingPostalCode"
                                value={checkoutInfo.shippingPostalCode}
                                onChange={handleCheckoutInfoChange}
                            />
                            </div>
                        </div>
                        </>
                    )}

                    {checkoutInfo.fulfillmentMethod === "pickup" &&
                    storeSettings?.pickupInstructions && (
                        <div className="alert alert-light border small">
                        <strong>Pickup Instructions:</strong>
                        <br />
                        {storeSettings.pickupInstructions}
                        </div>
                    )}

                    <hr />

                    <div className="mb-3">
                        <label className="form-label">
                        Order Notes
                        </label>

                        <textarea
                        className="form-control"
                        rows={3}
                        name="orderNotes"
                        value={checkoutInfo.orderNotes}
                        onChange={handleCheckoutInfoChange}
                        />
                    </div>

                    {checkoutError && (
                    <div className="alert alert-danger small">
                        {checkoutError}
                    </div>
                    )}

                    <div className="alert alert-info small">
                    You will be redirected to Stripe to securely complete payment.
                    Inventory is finalized after payment confirmation.
                    </div>

                    <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={checkoutLoading}
                    onClick={handleCheckout}
                    >
                    {checkoutLoading ? "Starting Checkout..." : "Checkout with Stripe"}
                    </button>

                    <Link
                        to={`/${eventslug}/store`}
                        className="btn btn-outline-primary w-100 mt-2"
                    >
                        Continue Shopping
                    </Link>

                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  </>
  );
};

export default StoreCart;
